/**
 * Каталог атомов прав (ADR-036, 06-roles-permissions §2). Коды стабильны: хранятся в БД
 * (`role_permission.permission`) и проверяются API; переименование — только миграцией.
 *
 * `since` — фаза, с которой право начинает действовать. Backoffice показывает только действующие
 * права ({@link isPermissionActive}); каталог держится полным, чтобы коды и пресеты не менялись
 * от фазы к фазе.
 * `reason` — действие требует причины и пишется в AuditLog (★ в 06).
 * `logged` — каждое действие журналируется (● в 06).
 */
export const PHASES = [
    'phase0',
    'phase1a',
    'phase1b',
    'phase2',
    'phase3',
    'phase5',
    'phase7',
];
/** Фаза текущего релиза: права с более поздним `since` ещё ни на что не влияют. */
export const CURRENT_PHASE = 'phase1a';
const catalog = {
    'order.view': { group: 'orders', since: 'phase1a' },
    'order.create': { group: 'orders', since: 'phase1a' },
    'order.edit': { group: 'orders', since: 'phase1a' },
    'order.cancel': { group: 'orders', since: 'phase1a', reason: true },
    /** Отмена с начала производства (ADR-053); до него достаточно `order.cancel`. */
    'order.cancel.in_production': { group: 'orders', since: 'phase1a', reason: true },
    'order.status.manual': { group: 'orders', since: 'phase1a', reason: true },
    'order.price.override': { group: 'orders', since: 'phase1a', reason: true },
    'order.discount.apply': { group: 'orders', since: 'phase1a' },
    'order.discount.over_limit': { group: 'orders', since: 'phase1a', reason: true },
    'order.reschedule': { group: 'orders', since: 'phase1a' },
    'order.issue': { group: 'orders', since: 'phase1a' },
    'order.repeat': { group: 'orders', since: 'phase1b' },
    /** Минимальный gate Phase 1a (ADR-048): обход оплаты и макета. */
    'production.gate.override': { group: 'gates', since: 'phase1a', reason: true },
    'qc.override': { group: 'gates', since: 'phase1b', reason: true },
    'design.gate.override': { group: 'gates', since: 'phase1b', reason: true },
    'pickup.early_release': { group: 'gates', since: 'phase1b', reason: true },
    'payment.accept': { group: 'payments', since: 'phase1a' },
    /** Сторно ручных оплат (ADR-055); онлайн-возвраты — Phase 3. */
    'payment.refund': { group: 'payments', since: 'phase1a', reason: true },
    /** Сторно своей наличной оплаты в ещё открытой смене (ADR-055). */
    'payment.void_same_session': { group: 'payments', since: 'phase1a', reason: true },
    'cash.open': { group: 'payments', since: 'phase1a' },
    'cash.close': { group: 'payments', since: 'phase1a' },
    'invoice.create': { group: 'payments', since: 'phase3' },
    'invoice.void': { group: 'payments', since: 'phase3', reason: true },
    'file.upload': { group: 'files', since: 'phase1a' },
    'file.view_preview': { group: 'files', since: 'phase1a' },
    'file.download.original': { group: 'files', since: 'phase1a', logged: true },
    'file.download.production': { group: 'files', since: 'phase1a', logged: true },
    'file.release_to_customer': { group: 'files', since: 'phase7', reason: true },
    'layout.upload': { group: 'layouts', since: 'phase1a' },
    'layout.send_for_approval': { group: 'layouts', since: 'phase1b' },
    'layout.approve_on_behalf': { group: 'layouts', since: 'phase1b', reason: true },
    'production.queue.view': { group: 'production', since: 'phase1a' },
    'production.task.start': { group: 'production', since: 'phase1a' },
    'production.task.complete': { group: 'production', since: 'phase1a' },
    'production.label.print': { group: 'production', since: 'phase1a' },
    'qc.finished.record': { group: 'production', since: 'phase2' },
    'stock.view': { group: 'stock', since: 'phase2' },
    'stock.receive': { group: 'stock', since: 'phase2' },
    'stock.write_off': { group: 'stock', since: 'phase2', reason: true },
    'stock.adjust': { group: 'stock', since: 'phase2', reason: true },
    'stock.stocktake': { group: 'stock', since: 'phase2' },
    'transfer.create': { group: 'stock', since: 'phase2' },
    'transfer.send': { group: 'stock', since: 'phase1a' },
    'transfer.receive': { group: 'stock', since: 'phase1a' },
    'customer.view': { group: 'customers', since: 'phase1a' },
    'customer.edit': { group: 'customers', since: 'phase1a' },
    'customer.merge': { group: 'customers', since: 'phase1a', reason: true },
    'company.edit': { group: 'customers', since: 'phase3' },
    'corporate.credit_limit.edit': { group: 'customers', since: 'phase3', reason: true },
    'corporate.allow_unpaid_production': { group: 'customers', since: 'phase3', reason: true },
    'ai.inbox.view': { group: 'ai', since: 'phase5' },
    'ai.task.approve': { group: 'ai', since: 'phase5' },
    'ai.reply.send': { group: 'ai', since: 'phase5' },
    'ai.automation.configure': { group: 'ai', since: 'phase5', reason: true },
    'catalog.manage': { group: 'admin', since: 'phase1a' },
    'pricing.manage': { group: 'admin', since: 'phase1a' },
    'points.manage': { group: 'admin', since: 'phase0' },
    'users.manage': { group: 'admin', since: 'phase0' },
    'roles.manage': { group: 'admin', since: 'phase1a' },
    'settings.manage': { group: 'admin', since: 'phase0' },
    'templates.manage': { group: 'admin', since: 'phase1b' },
    'integrations.manage': { group: 'admin', since: 'phase1b' },
    'reports.view': { group: 'admin', since: 'phase1b' },
    'audit.view': { group: 'admin', since: 'phase0' },
    'system.diagnostics': { group: 'admin', since: 'phase0' },
};
export const PERMISSIONS = Object.keys(catalog);
export const permissionCatalog = catalog;
export function isPermission(code) {
    return Object.hasOwn(catalog, code);
}
export function isPermissionActive(permission, phase = CURRENT_PHASE) {
    return PHASES.indexOf(permissionCatalog[permission].since) <= PHASES.indexOf(phase);
}
/**
 * Роли-пресеты (06 §3). Создаются миграциями во всех инстансах; администратор назначает их
 * сотрудникам. Миграции прав только добавляют (ADR-036): правка этого списка требует новой
 * миграции через `grant_preset_permission` — пресет, изменённый администратором, получает
 * право не сразу, а предложением в редакторе ролей (ADR-057). Тест сверяет пресеты в чистой БД
 * с этим описанием.
 *
 * Администратор — без списка: у него всегда весь каталог, назначается только на все точки.
 */
export const ROLE_PRESETS = [
    'admin',
    'manager',
    'point_staff',
    'production',
    'warehouse',
    'logistics',
    'quality',
    'accounting',
    'designer',
    'courier',
    'partner',
];
export const rolePresets = {
    admin: { requiresMfa: true, permissions: 'all' },
    manager: {
        requiresMfa: true,
        permissions: [
            'order.view',
            'order.create',
            'order.edit',
            'order.cancel',
            'order.cancel.in_production',
            'order.status.manual',
            'order.price.override',
            'order.discount.apply',
            'order.discount.over_limit',
            'order.reschedule',
            'order.issue',
            'order.repeat',
            'production.gate.override',
            'design.gate.override',
            'pickup.early_release',
            'payment.accept',
            'payment.refund',
            'payment.void_same_session',
            'cash.open',
            'cash.close',
            'invoice.create',
            'file.upload',
            'file.view_preview',
            'file.download.original',
            'layout.upload',
            'layout.send_for_approval',
            'layout.approve_on_behalf',
            'production.queue.view',
            'stock.view',
            'customer.view',
            'customer.edit',
            'customer.merge',
            'company.edit',
            'ai.inbox.view',
            'ai.task.approve',
            'ai.reply.send',
            'reports.view',
        ],
    },
    point_staff: {
        requiresMfa: false,
        permissions: [
            'order.view',
            'order.create',
            'order.edit',
            'order.cancel',
            'order.discount.apply',
            'order.reschedule',
            'order.issue',
            'order.repeat',
            'payment.accept',
            'payment.void_same_session',
            'cash.open',
            'cash.close',
            'file.upload',
            'file.view_preview',
            'file.download.original',
            'layout.upload',
            'layout.send_for_approval',
            'production.queue.view',
            'production.task.start',
            'production.task.complete',
            'stock.view',
            'transfer.send',
            'transfer.receive',
            'customer.view',
            'customer.edit',
            'ai.inbox.view',
            'ai.task.approve',
            'ai.reply.send',
            'reports.view',
        ],
    },
    production: {
        requiresMfa: false,
        permissions: [
            'order.view',
            'file.view_preview',
            'file.download.original',
            'file.download.production',
            'production.queue.view',
            'production.task.start',
            'production.task.complete',
            'production.label.print',
            'order.reschedule',
            'transfer.send',
        ],
    },
    warehouse: {
        requiresMfa: false,
        permissions: [
            'stock.view',
            'stock.receive',
            'stock.write_off',
            'stock.adjust',
            'stock.stocktake',
            'transfer.create',
            'transfer.send',
            'transfer.receive',
            'reports.view',
        ],
    },
    logistics: {
        requiresMfa: false,
        permissions: ['stock.view', 'transfer.create', 'transfer.send', 'transfer.receive'],
    },
    quality: {
        requiresMfa: false,
        permissions: [
            'order.view',
            'qc.override',
            'qc.finished.record',
            'file.view_preview',
            'file.download.original',
            'file.download.production',
            'production.queue.view',
        ],
    },
    accounting: {
        requiresMfa: true,
        // Журнал аудита — только Администратор (ответ владельца В6); выдаётся ролям через редактор.
        permissions: [
            'order.view',
            'payment.accept',
            'payment.refund',
            'invoice.create',
            'invoice.void',
            'customer.view',
            'company.edit',
            'corporate.credit_limit.edit',
            'reports.view',
        ],
    },
    designer: {
        requiresMfa: false,
        permissions: [
            'order.view',
            'file.upload',
            'file.view_preview',
            'file.download.original',
            'file.download.production',
            'layout.upload',
            'layout.send_for_approval',
        ],
    },
    courier: { requiresMfa: false, permissions: ['transfer.send', 'transfer.receive'] },
    partner: {
        requiresMfa: false,
        permissions: ['production.task.start', 'production.task.complete', 'file.download.production'],
    },
};
//# sourceMappingURL=permissions.js.map